/**
 * RaceTrace Live, Strategy: the range of places the focus car is likely to finish in, and the
 * cars that decide which end of it it gets.
 *
 * The range is the projected place (live-strategy.ts projPos) widened by how far finishes landed
 * from the projection on the series' 2026 races, at the same time to go (FINISH_RANGES): the places
 * 8 in 10 finishes fell within, one race left out at a time (split-conformal: the 10th and 90th
 * percentiles of the held-out errors). Held-out coverage 83-90% (WRL) and 83-85% (WeatherTech).
 *
 * The better end: the running cars projected ahead of it within the range, nearest first, with the
 * gap at the flag on today's pace and the lap time that closes it (when the car has run that pace
 * lately: live-battles inReach), and whether a stop under a caution would close it. The worse end:
 * the running cars projected behind it within the range, with the time a lap it has in hand.
 * What it takes, not advice to do it.
 */
import type { RaceChartData } from "@shared/types";
import { PILOT_CHALLENGE_CAUTION_RATES, WEATHERTECH_CAUTION_RATES, type CarStrategy, type CautionRates, type LiveStrategy } from "./live-strategy";
import { inReach, recentPace } from "./live-battles";

export interface FinishRangeBand {
  /** The band applies with more than this much time to go (s). */
  overS: number;
  /** Places better (negative) and worse than the projection that 8 in 10 finishes fell within. */
  lo: number;
  hi: number;
}

/** By series, the bands from the most time to go to the least. */
export const WRL_FINISH_RANGE: FinishRangeBand[] = [
  { overS: 4 * 3600, lo: -4, hi: 3 },
  { overS: 2 * 3600, lo: -2, hi: 1 },
  { overS: 3600, lo: -1, hi: 1 },
  { overS: 0, lo: -1, hi: 1 },
];
export const WEATHERTECH_FINISH_RANGE: FinishRangeBand[] = [
  { overS: 2 * 3600, lo: -5, hi: 4 },
  { overS: 3600, lo: -4, hi: 4 },
  { overS: 0, lo: -3, hi: 3 },
];

/** The bands for the series' caution rates (cautionRatesFor): WeatherTech's, none for Pilot Challenge (no races fitted), else WRL's. */
export function finishRangeBandsFor(rates: CautionRates | undefined): FinishRangeBand[] | null {
  if (rates === WEATHERTECH_CAUTION_RATES) return WEATHERTECH_FINISH_RANGE;
  if (rates === PILOT_CHALLENGE_CAUTION_RATES) return null;
  return WRL_FINISH_RANGE;
}

export interface RangeCar {
  num: number;
  /** Its projected class place. */
  projPos: number;
  /** Seconds between the two at the flag on today's pace (always positive). */
  gapS: number;
  /** Seconds a lap: to close the gap (ahead), or in hand (behind). */
  perLapS: number;
  /** Ahead: the lap time that closes it, when the car has run that pace lately; null when it has not. */
  targetS: number | null;
  beyondPace: boolean;
  /** Ahead: a stop under a caution would save at least the gap (it owes a stop). */
  cautionStopCloses: boolean;
}

export interface FinishRange {
  best: number;
  worst: number;
  /** Cars projected ahead within the range, nearest first (at most 2). */
  ahead: RangeCar[];
  /** Cars projected behind within the range, nearest first (at most 2). */
  behind: RangeCar[];
}

export function finishRange(s: Pick<LiveStrategy, "cars" | "timeToGoS" | "greenStopS" | "cautionStopS">, c: CarStrategy, data: RaceChartData, bands: FinishRangeBand[] | null): FinishRange | null {
  if (!bands || c.projPos === null || c.projGapS === null || c.stopped || c.lapsLeft === null || !(c.lapsLeft > 0)) return null;
  const ttg = s.timeToGoS ?? Infinity;
  const band = bands.find((b) => ttg > b.overS) ?? bands[bands.length - 1];
  const inClass = s.cars.length;
  const best = Math.max(1, c.projPos + band.lo);
  const worst = Math.min(inClass, c.projPos + band.hi);
  const laps = c.lapsLeft;
  const pace = recentPace(data, c.num);
  const saves = s.greenStopS !== null && s.cautionStopS !== null ? Math.max(0, s.greenStopS - s.cautionStopS) : 0;
  const running = s.cars.filter((o) => o !== c && !o.stopped && o.projPos !== null && o.projGapS !== null);
  const ahead = running
    .filter((o) => o.projPos! < c.projPos! && o.projPos! >= best)
    .sort((a, b) => b.projPos! - a.projPos!)
    .slice(0, 2)
    .map((o): RangeCar => {
      const gapS = Math.max(0, c.projGapS! - o.projGapS!);
      const perLapS = gapS / laps;
      const r = pace.now !== null ? inReach(pace.now - perLapS, pace.best) : { targetS: null, beyondPace: false };
      return { num: o.num, projPos: o.projPos!, gapS, perLapS, targetS: r.targetS, beyondPace: r.beyondPace, cautionStopCloses: (c.stopsToGo ?? 0) > 0 && saves > 0 && saves >= gapS };
    });
  const behind = running
    .filter((o) => o.projPos! > c.projPos! && o.projPos! <= worst)
    .sort((a, b) => a.projPos! - b.projPos!)
    .slice(0, 2)
    .map((o): RangeCar => {
      const gapS = Math.max(0, o.projGapS! - c.projGapS!);
      return { num: o.num, projPos: o.projPos!, gapS, perLapS: gapS / laps, targetS: null, beyondPace: false, cautionStopCloses: false };
    });
  return { best, worst, ahead, behind };
}
